import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {};

const slice = createSlice({
  name: 'blogs',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'blogsApi',
  baseQuery,
  tagTypes: ['Blogs', 'Tags', 'Categories', 'SubCategories'],
  endpoints: builder => ({
    getBlogsList: builder.query({
      query: () => ({
        url: endpoints.BLOGS + '/list',
        method: 'GET',
      }),
      providesTags: ['Blogs'],
    }),
    addBlogs: builder.mutation({
      query: data => ({
        url: endpoints.BLOGS + '/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Blogs'],
    }),
    deleteBlogs: builder.mutation({
      query: id => ({
        url: endpoints.BLOGS + '/delete/' + id,
        method: 'DELETE',
      }),
      invalidatesTags: ['Blogs'],
    }),
    getBlogs: builder.query({
      query: id => ({
        url: endpoints.BLOGS + '/' + id,
        method: 'GET',
      }),
    }),
    getBlogById: builder.query({
      query: id => ({
        url: endpoints.BLOGS + '/' + id,
        method: 'GET',
      }),
    }),
    getCategories: builder.query({
      query: () => ({
        url: endpoints.BLOGS_CATEGORIES + '/list',
        method: 'GET',
      }),
      providesTags: ['Categories'],
    }),
    getAllCategories: builder.query({
      query: () => ({
        url: endpoints.BLOGS_CATEGORIES + '/all',
        method: 'GET',
      }),
      providesTags: ['Categories'],
    }),
    getTags: builder.query({
      query: () => ({
        url: endpoints.BLOGS_TAGS,
        method: 'GET',
      }),
      providesTags: ['Tags'],
    }),
    addTags: builder.mutation({
      query: data => ({
        url: endpoints.BLOGS_TAGS + '/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Tags'],
    }),
    deleteTags: builder.mutation({
      query: id => ({
        url: endpoints.BLOGS_TAGS + '/delete/' + id,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tags'],
    }),
    addCategories: builder.mutation({
      query: data => ({
        url: endpoints.BLOGS_CATEGORIES + '/add',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Categories', 'SubCategories'],
    }),
    deleteCategories: builder.mutation({
      query: id => ({
        url: endpoints.BLOGS_CATEGORIES + '/delete/' + id,
        method: 'DELETE',
      }),
      invalidatesTags: ['Categories', 'SubCategories'],
    }),
    updateCategories: builder.mutation({
      query: data => ({
        url: endpoints.BLOGS_CATEGORIES + '/update',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Categories', 'SubCategories'],
    }),
    getSubCategories: builder.query({
      query: params => ({
        url: endpoints.BLOGS_CATEGORIES + '/sub-categories',
        method: 'GET',
        params,
      }),
      providesTags: ['SubCategories'],
    }),
  }),
});

export const { actions: BlogsAction } = slice;

export const useBlogsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: BlogsAction,
    useAddBlogsMutation: api.useAddBlogsMutation,
    useDeleteBlogsMutation: api.useDeleteBlogsMutation,
    useGetCategoriesQuery: api.useLazyGetCategoriesQuery,
    useGetAllCategoriesQuery: api.useLazyGetAllCategoriesQuery,
    useGetTagsQuery: api.useLazyGetTagsQuery,
    useAddTagsMutation: api.useAddTagsMutation,
    useDeleteTagsMutation: api.useDeleteTagsMutation,
    useAddCategoriesMutation: api.useAddCategoriesMutation,
    useDeleteCategoriesMutation: api.useDeleteCategoriesMutation,
    useUpdateCategoriesMutation: api.useUpdateCategoriesMutation,
    useGetBlogsListQuery: api.useLazyGetBlogsListQuery,
    useGetBlogsQuery: api.useLazyGetBlogsQuery,
    useGetSubCategoriesQuery: api.useLazyGetSubCategoriesQuery,
    useGetBlogByIdQuery: api.useLazyGetBlogByIdQuery,
  };
};
